import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["closeable", "inline", "multiline", "use", "_flexWrapperStyles", "_avatar", "href", "target", "onClick", "onCloseClick", "id", "role", "aria-label", "truncateStringProps", "children"];
import { useRef } from 'react';
import styled from 'foundations-theming/utils';
import { uniqueId } from '../../utils/uniqueId';
import { CloseButton } from '../CloseButton/CloseButton';
import { Clickable } from '../Clickable/Clickable';
import { Link } from '../Link/Link';
import { TruncateString } from '../TruncateString/TruncateString';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { TagProvider } from './TagProvider';
import { useStatusTagContext } from '../StatusTag/StatusTagProvider';
import { deepMerge } from '../../utils/deepMerge';
import { fwcColor, fwcBorderRadius } from 'foundations-theming/utils/fwc';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { useTheme } from 'foundations-theming/utils';

// 'default' and 'subtle' are structural variants, not color families.
// 'heffalump' and 'olaf' are deprecated legacy colors with no meaningful saturated equivalent.
// 'teal-light' exists only as a regular tag color; badge uses 'badge-teal-dark' instead.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BADGE_PREFIX = 'badge-';
const isBadgeStyle = use => use.startsWith(BADGE_PREFIX);
const backgroundColorMapping = {
  // Regular tag backgrounds (subtle)
  calypso: fwcColor('fwc-tag-fill-calypso'),
  'candy-apple': fwcColor('fwc-tag-fill-candy-apple'),
  heffalump: fwcColor('fwc-tag-fill-heffalump'),
  koala: fwcColor('fwc-tag-fill-koala'),
  lorax: fwcColor('fwc-tag-fill-lorax'),
  marigold: fwcColor('fwc-tag-fill-marigold'),
  norman: fwcColor('fwc-tag-fill-norman'),
  olaf: fwcColor('fwc-tag-fill-olaf'),
  oz: fwcColor('fwc-tag-fill-oz'),
  pantera: fwcColor('fwc-tag-fill-pantera'),
  sorbet: fwcColor('fwc-tag-fill-sorbet'),
  thunderdome: fwcColor('fwc-tag-fill-thunderdome'),
  default: fwcColor('fwc-tag-fill-default'),
  subtle: fwcColor('fwc-tag-fill-subtle'),
  blue: fwcColor('fwc-tag-fill-blue'),
  'teal-light': fwcColor('fwc-tag-fill-teal-light'),
  'teal-dark': fwcColor('fwc-tag-fill-teal-dark'),
  teal: fwcColor('fwc-tag-fill-teal'),
  green: fwcColor('fwc-tag-fill-green'),
  yellow: fwcColor('fwc-tag-fill-yellow'),
  orange: fwcColor('fwc-tag-fill-orange'),
  magenta: fwcColor('fwc-tag-fill-magenta'),
  purple: fwcColor('fwc-tag-fill-purple'),
  red: fwcColor('fwc-tag-fill-red'),
  // Badge backgrounds — simple color names
  'badge-blue': fwcColor('fwc-tag-fill-badge-blue'),
  'badge-teal': fwcColor('fwc-tag-fill-badge-teal'),
  'badge-green': fwcColor('fwc-tag-fill-badge-green'),
  'badge-yellow': fwcColor('fwc-tag-fill-badge-yellow'),
  'badge-orange': fwcColor('fwc-tag-fill-badge-orange'),
  'badge-magenta': fwcColor('fwc-tag-fill-badge-magenta'),
  'badge-purple': fwcColor('fwc-tag-fill-badge-purple'),
  'badge-red': fwcColor('fwc-tag-fill-badge-red'),
  // Badge backgrounds — legacy colors (saturated)
  'badge-calypso': fwcColor('fwc-tag-fill-badge-calypso'),
  'badge-candy-apple': fwcColor('fwc-tag-fill-badge-candy-apple'),
  'badge-lorax': fwcColor('fwc-tag-fill-badge-lorax'),
  'badge-marigold': fwcColor('fwc-tag-fill-badge-marigold'),
  'badge-norman': fwcColor('fwc-tag-fill-badge-norman'),
  'badge-pantera': fwcColor('fwc-tag-fill-badge-pantera'),
  'badge-oz': fwcColor('fwc-tag-fill-badge-oz'),
  'badge-koala': fwcColor('fwc-tag-fill-badge-koala'),
  'badge-sorbet': fwcColor('fwc-tag-fill-badge-sorbet'),
  'badge-thunderdome': fwcColor('fwc-tag-fill-badge-thunderdome'),
  'badge-teal-dark': fwcColor('fwc-tag-fill-badge-teal-dark')
};
const FWC_SATURATED_FILL_COLORS = ['calypso', 'candy-apple', 'lorax', 'marigold', 'norman', 'oz', 'pantera', 'sorbet', 'teal-dark', 'thunderdome'];
const SATURATED_FILL_COLORS = ['badge-calypso', 'badge-candy-apple', 'badge-lorax', 'badge-marigold', 'badge-norman', 'badge-pantera', 'badge-oz', 'badge-sorbet', 'badge-teal-dark', 'badge-thunderdome'];
const borderColorMapping = {
  calypso: fwcColor('fwc-tag-border-calypso'),
  'candy-apple': fwcColor('fwc-tag-border-candy-apple'),
  heffalump: fwcColor('fwc-tag-border-heffalump'),
  koala: fwcColor('fwc-tag-border-koala'),
  lorax: fwcColor('fwc-tag-border-lorax'),
  marigold: fwcColor('fwc-tag-border-marigold'),
  norman: fwcColor('fwc-tag-border-norman'),
  olaf: fwcColor('fwc-tag-border-olaf'),
  oz: fwcColor('fwc-tag-border-oz'),
  pantera: fwcColor('fwc-tag-border-pantera'),
  sorbet: fwcColor('fwc-tag-border-sorbet'),
  thunderdome: fwcColor('fwc-tag-border-thunderdome'),
  default: fwcColor('fwc-tag-border-default'),
  subtle: fwcColor('fwc-tag-border-subtle'),
  blue: fwcColor('fwc-tag-border-blue'),
  'teal-light': fwcColor('fwc-tag-border-teal-light'),
  'teal-dark': fwcColor('fwc-tag-border-teal-dark'),
  teal: fwcColor('fwc-tag-border-teal'),
  green: fwcColor('fwc-tag-border-green'),
  yellow: fwcColor('fwc-tag-border-yellow'),
  orange: fwcColor('fwc-tag-border-orange'),
  magenta: fwcColor('fwc-tag-border-magenta'),
  purple: fwcColor('fwc-tag-border-purple'),
  red: fwcColor('fwc-tag-border-red'),
  // Badge borders — simple color names
  'badge-blue': fwcColor('fwc-tag-border-badge-blue'),
  'badge-teal': fwcColor('fwc-tag-border-badge-teal'),
  'badge-green': fwcColor('fwc-tag-border-badge-green'),
  'badge-yellow': fwcColor('fwc-tag-border-badge-yellow'),
  'badge-orange': fwcColor('fwc-tag-border-badge-orange'),
  'badge-magenta': fwcColor('fwc-tag-border-badge-magenta'),
  'badge-purple': fwcColor('fwc-tag-border-badge-purple'),
  'badge-red': fwcColor('fwc-tag-border-badge-red'),
  // Badge borders — legacy colors (saturated)
  'badge-calypso': fwcColor('fwc-tag-border-badge-calypso'),
  'badge-candy-apple': fwcColor('fwc-tag-border-badge-candy-apple'),
  'badge-lorax': fwcColor('fwc-tag-border-badge-lorax'),
  'badge-marigold': fwcColor('fwc-tag-border-badge-marigold'),
  'badge-norman': fwcColor('fwc-tag-border-badge-norman'),
  'badge-pantera': fwcColor('fwc-tag-border-badge-pantera'),
  'badge-oz': fwcColor('fwc-tag-border-badge-oz'),
  'badge-koala': fwcColor('fwc-tag-border-badge-koala'),
  'badge-sorbet': fwcColor('fwc-tag-border-badge-sorbet'),
  'badge-thunderdome': fwcColor('fwc-tag-border-badge-thunderdome'),
  'badge-teal-dark': fwcColor('fwc-tag-border-badge-teal-dark')
};
const STATUS_ALIAS_MAP = {
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  active: 'success',
  sent: 'success',
  published: 'success',
  inReview: 'warning',
  scheduled: 'warning',
  removed: 'danger',
  cancelled: 'danger',
  error: 'danger'
};
export const USE_COLOR_ALIAS_MAP = {
  // defaults
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  // oz
  success: 'oz',
  active: 'oz',
  sent: 'oz',
  published: 'oz',
  // marigold
  warning: 'marigold',
  inReview: 'marigold',
  scheduled: 'marigold',
  // cand-apple
  danger: 'candy-apple',
  removed: 'candy-apple',
  cancelled: 'candy-apple',
  error: 'candy-apple',
  // calypso
  info: 'calypso'
};
export const getBackgroundColorToken = use => {
  var _use;
  return (_use = backgroundColorMapping[use]) !== null && _use !== void 0 ? _use : fwcColor('fwc-tag-fill-pantera');
};
export const getBorderColorToken = use => {
  var _ref;
  return (_ref = borderColorMapping[use]) !== null && _ref !== void 0 ? _ref : 'fill-transparent-default';
};
const getTextColorToken = use => {
  const isSaturatedFill = SATURATED_FILL_COLORS.includes(use);
  const isFwcSaturatedFill = FWC_SATURATED_FILL_COLORS.includes(use);
  if (isSaturatedFill) {
    // Yellow-family saturated backgrounds are light, so they require dark text
    // rather than the white 'text-core-on-fill-default' used by other saturated fills.
    if (use === 'badge-marigold') {
      return fwcColor('fwc-tag-text-marigold');
    }
    if (use === 'badge-yellow') {
      return 'text-inverse-default';
    }
    return 'text-core-on-fill-default';
  }
  if (isFwcSaturatedFill) {
    return fwcColor('fwc-tag-text-onfill');
  }
  const baseKey = isBadgeStyle(use) ? use.slice(BADGE_PREFIX.length) : use;
  if (baseKey === 'olaf') {
    return 'text-core-on-fill-default';
  }
  if (baseKey === 'subtle') {
    return fwcColor('fwc-tag-text-subtle');
  }
  return 'text-core-default';
};
export const StyledTag = styled.span.withConfig({
  displayName: "Tag__StyledTag"
})(({
  $computedUse,
  $closeable,
  $inline,
  $isBadge,
  $cssStyles,
  theme,
  $styleOverrides
}) => {
  const tokenizedBackgroundColor = getBackgroundColorToken($computedUse);
  const tokenizedBorderColor = getBorderColorToken($computedUse);
  const hasExplicitMarginInlineStart = ('marginInlineStart' in $cssStyles);
  const isSubtle = $computedUse === 'subtle';
  const typographyStyle = isSubtle ? 'detail-200' : 'heading-25';
  const {
    fontFamily,
    fontSize,
    fontWeight
  } = theme.typography[typographyStyle];
  const borderRadius = $isBadge ? theme.borderRadius[fwcBorderRadius('fwc-tag-badge')] : theme.borderRadius[fwcBorderRadius('fwc-tag')];
  const borderColor = theme.color[tokenizedBorderColor];
  return deepMerge(Object.assign({
    fontFamily,
    fontSize,
    fontWeight,
    backgroundColor: theme.color[tokenizedBackgroundColor],
    borderBlockWidth: theme.borderWidth['100'],
    borderInlineWidth: theme.borderWidth['100'],
    borderBlockStyle: 'solid',
    borderInlineStyle: 'solid',
    borderBlockColor: borderColor,
    borderInlineColor: borderColor,
    borderStartStartRadius: borderRadius,
    borderStartEndRadius: borderRadius,
    borderEndEndRadius: borderRadius,
    borderEndStartRadius: borderRadius,
    color: theme.color[getTextColorToken($computedUse)],
    // No line-height token matches these values; hardcoded to match Badge component sizing.
    lineHeight: $isBadge ? '18px' : '22px',
    paddingBlock: theme.space['0'],
    paddingInline: $isBadge ? `calc(${theme.space['200']} - ${theme.borderWidth['100']})` : theme.space['200'],
    position: 'relative',
    cursor: 'inherit',
    display: 'inline-flex',
    maxInlineSize: '100%',
    overflow: 'hidden',
    userSelect: 'none',
    verticalAlign: $inline ? 'middle' : theme.space['200'],
    paddingInlineEnd: $closeable ? $isBadge ? theme.space['600'] : theme.space['700'] : undefined,
    marginBlock: $inline ? theme.space['0'] : undefined,
    marginInline: $inline ? theme.space['100'] : undefined,
    /* Applies spacing between inner elements (ex. text and icon) */
    '& > * + *': {
      marginInlineStart: theme.space['200']
    },
    /* Applies spacing between sibling tags */
    '& + &': {
      marginInlineStart: $inline ? undefined : theme.space['300']
    }
  }, hasExplicitMarginInlineStart && {
    [CSS_SPECIFICITY.OVERRIDE]: {
      marginInlineStart: $cssStyles.marginInlineStart
    }
  }, {
    /* Don't color links in a tag */
    '& a, & a:hover, & a:active': {
      color: 'inherit'
    }
  }, $cssStyles), $styleOverrides);
});
const FlexWrapper = styled.span.withConfig({
  displayName: "Tag__FlexWrapper"
})(({
  $cssStyles
}) => {
  return Object.assign({
    display: 'inline-flex',
    maxInlineSize: '100%',
    verticalAlign: 'top'
  }, $cssStyles);
});
const ContentWrapper = styled.span.withConfig({
  displayName: "Tag__ContentWrapper"
})(() => {
  return {
    overflow: 'hidden'
  };
});
const getCloseButtonStyleOverrides = isBadge => ({
  insetBlockStart: '0',
  insetBlockEnd: '0',
  insetInlineEnd: isBadge ? '0px' : '6px',
  color: 'currentColor',
  marginBlockStart: 'auto',
  marginBlockEnd: 'auto',
  position: 'absolute',
  transform: 'scale(0.5)'
});
const StyledClickable = styled(Clickable)(({
  theme
}) => {
  return {
    [CSS_SPECIFICITY.OVERRIDE]: {
      fontWeight: theme.typography['heading-200'].fontWeight,
      overflow: 'hidden',
      textDecoration: 'underline'
    }
  };
});
const renderContents = (href, target, onClick, truncatedChildren) => {
  if (href) {
    return /*#__PURE__*/_jsx(Link, {
      href: href,
      onClick: onClick,
      target: target,
      use: "on-bright",
      children: truncatedChildren
    });
  } else if (onClick) {
    return /*#__PURE__*/_jsx(StyledClickable, {
      onClick: onClick,
      children: truncatedChildren
    });
  }
  return /*#__PURE__*/_jsx(ContentWrapper, {
    children: truncatedChildren
  });
};
export const TAG_COMPONENT_NAME = 'UITag';
export const Tag = _ref2 => {
  var _statusTagContext$tag;
  let {
      closeable = false,
      inline = false,
      multiline = false,
      use = 'default',
      _flexWrapperStyles = {},
      _avatar,
      href,
      target,
      onClick,
      onCloseClick,
      id,
      role,
      'aria-label': ariaLabel,
      truncateStringProps,
      children
    } = _ref2,
    restProps = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(restProps);
  const containerCssStyles = useMapTokenPropsToCSS(tokenProps);
  const flexWrapperCssStyles = useMapTokenPropsToCSS(_flexWrapperStyles);
  const statusTagContext = useStatusTagContext();
  const theme = useTheme();
  const idRef = useRef(uniqueId('tag-'));
  const computedId = id || idRef.current;
  const isBadge = isBadgeStyle(use);
  const computedUse = USE_COLOR_ALIAS_MAP[use] || use;
  const isSubtle = use === 'subtle';
  const linkStyleOverrides = Object.assign({
    overflow: 'hidden',
    // Override the global textUnderlineOffset style for tags to inherit from the parent element
    textUnderlineOffset: 'inherit'
  }, isSubtle && {
    fontSize: theme.typography['detail-200'].fontSize,
    fontFamily: theme.typography['detail-200'].fontFamily,
    fontWeight: theme.typography['detail-200'].fontWeight
  });
  const truncatedChildren = multiline ? children : /*#__PURE__*/_jsx(TruncateString, Object.assign({
    useFlex: true
  }, truncateStringProps, {
    children: children
  }));
  return /*#__PURE__*/_jsx(StyledTag
  /**
   * "UITag" is intentional to provide backwards compatibility with Selenium helpers
   * @see https://github.com/HubSpotEngineering/foundations-components/issues/5187
   * @see https://github.com/HubSpotEngineering/hs-selenium/blob/master/selenium-js-utils/src/UIComponents/select/internal/core.ts#L25
   */, Object.assign({
    "data-component-name": TAG_COMPONENT_NAME,
    "data-tag-use": use,
    id: computedId,
    role: role,
    "aria-label": ariaLabel,
    $computedUse: computedUse,
    $closeable: closeable,
    $inline: inline,
    $isBadge: isBadge,
    $cssStyles: containerCssStyles,
    $styleOverrides: (_statusTagContext$tag = statusTagContext === null || statusTagContext === void 0 ? void 0 : statusTagContext.tagStyleOverrides) !== null && _statusTagContext$tag !== void 0 ? _statusTagContext$tag : {}
  }, domProps, callbackProps, {
    children: /*#__PURE__*/_jsxs(TagProvider, {
      closeButtonStyleOverrides: getCloseButtonStyleOverrides(isBadge),
      linkStyleOverrides: linkStyleOverrides,
      children: [/*#__PURE__*/_jsxs(FlexWrapper, {
        $cssStyles: flexWrapperCssStyles,
        children: [_avatar, renderContents(href, target, onClick, truncatedChildren)]
      }), closeable && /*#__PURE__*/_jsx(CloseButton, {
        color: "icon-core-default",
        "aria-describedby": computedId,
        onClick: onCloseClick
      })]
    })
  }));
};
Tag.displayName = 'Tag';